import { toNumber } from 'lodash';
import { FIRM_SUMMARY_DRAFT_TITLE, FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX } from 'pages/Firms/common/constants';
import { generateColumnKey, getArrayValue, getObjectValue, shortDate } from 'utilities';
import { CreateColumnsParams, FirmSummaryColumn } from './createColumns.types';

const createColumns = (params: CreateColumnsParams): FirmSummaryColumn[] => {
  const { fundsByCompany } = params;

  // Fund
  const fund: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const { company_name: companyName, fund_name: fundName } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: fundName,
      },
    };
  }, {} as FirmSummaryColumn);

  // Valuation date
  const valuationDate: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_name: fundName,
      measurement_date: fundValuationDate,
    } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: shortDate(fundValuationDate),
      },
    };
  }, {} as FirmSummaryColumn);

  // Initial investment date
  const initialInvestmentDate: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_name: fundName,
      initial_investment_date: fundInitialInvestmentDate,
    } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: shortDate(fundInitialInvestmentDate),
      },
    };
  }, {} as FirmSummaryColumn);

  // Most recent investment date
  const mostRecentInvestmentDate: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_name: fundName,
      most_recent_investment_date: fundMostRecentInvestmentDate,
    } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: shortDate(fundMostRecentInvestmentDate),
      },
    };
  }, {} as FirmSummaryColumn);

  // Fully diluted ownership percentage
  const fullyDilutedOwnershipPercentage: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce(
    (accumulator, current) => {
      const {
        company_name: companyName,
        diluted_ownership: fundFullyDilutedOwnershipPercentage,
        fund_name: fundName,
        funds_count: fundsCount,
      } = getObjectValue(current);

      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });

      return {
        ...accumulator,
        [columnKey]: {
          funds_count: fundsCount,
          value: toNumber(fundFullyDilutedOwnershipPercentage),
        },
      };
    },
    {} as FirmSummaryColumn
  );

  // Invested capital
  const investedCapital: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_cost_basis: fundinvestedCapital,
      fund_name: fundName,
      funds_count: fundsCount,
    } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        funds_count: fundsCount,
        value: toNumber(fundinvestedCapital),
      },
    };
  }, {} as FirmSummaryColumn);

  // Realized value
  const realizedValue: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_name: fundName,
      funds_count: fundsCount,
      is_final: isFinal,
      is_published: isPublished,
      realized_value: fundRealizedValue,
    } = getObjectValue(current);

    const isDraft = !isPublished && !isFinal;

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        funds_count: fundsCount,
        isDraft,
        value: toNumber(fundRealizedValue),
      },
    };
  }, {} as FirmSummaryColumn);

  // Unrealized value
  const unrealizedValue: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_name: fundName,
      funds_count: fundsCount,
      is_final: isFinal,
      is_published: isPublished,
      unrealized_value: fundUnrealizedValue,
    } = getObjectValue(current);

    const isDraft = !isPublished && !isFinal;

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        funds_count: fundsCount,
        isDraft,
        value: toNumber(fundUnrealizedValue),
      },
    };
  }, {} as FirmSummaryColumn);

  // Total value
  const totalValue: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_name: fundName,
      funds_count: fundsCount,
      is_final: isFinal,
      is_published: isPublished,
      total_value: fundTotalValue,
    } = getObjectValue(current);

    const isDraft = !isPublished && !isFinal;

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        draftValue: FIRM_SUMMARY_DRAFT_TITLE,
        funds_count: fundsCount,
        isDraft,
        value: toNumber(fundTotalValue),
      },
    };
  }, {} as FirmSummaryColumn);

  // Gross IRR
  const grossIRR: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_name: fundName,
      gross_irr: fundGrossIRR,
      is_final: isFinal,
      is_published: isPublished,
    } = getObjectValue(current);

    const isDraft = !isPublished && !isFinal;

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        isDraft,
        value: toNumber(fundGrossIRR),
      },
    };
  }, {} as FirmSummaryColumn);

  // MOIC
  const MOIC: FirmSummaryColumn = getArrayValue(fundsByCompany).reduce((accumulator, current) => {
    const {
      company_name: companyName,
      fund_cost_basis: fundinvestedCapital,
      fund_name: fundName,
      is_final: isFinal,
      is_published: isPublished,
      total_value: fundTotalValue,
    } = getObjectValue(current);

    const isDraft = !isPublished && !isFinal;
    const fundMOIC = toNumber(fundinvestedCapital) > 0 ? toNumber(fundTotalValue) / toNumber(fundinvestedCapital) : 0;

    const columnKey = generateColumnKey({
      id: companyName,
      name: fundName,
      prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        isDraft,
        value: toNumber(fundMOIC),
      },
    };
  }, {} as FirmSummaryColumn);

  const columns = [
    fund,
    valuationDate,
    initialInvestmentDate,
    mostRecentInvestmentDate,
    fullyDilutedOwnershipPercentage,
    investedCapital,
    realizedValue,
    unrealizedValue,
    totalValue,
    grossIRR,
    MOIC,
  ];

  return columns;
};

export default createColumns;
